import React, { useEffect, useState } from 'react';
import { Card } from './ui/Card';
import { Button } from './ui/Button';
import { XIcon, GripVerticalIcon, PlusIcon, RotateCcwIcon } from 'lucide-react';
import { useEvent } from '../contexts/EventContext';
import { useLanguage } from '../contexts/LanguageContext';
import { useQuickLinks, type QuickLink } from '../contexts/QuickLinksContext';
interface QuickLinksCustomizerProps {
  isOpen: boolean;
  onClose: () => void;
}
export function QuickLinksCustomizer({
  isOpen,
  onClose
}: QuickLinksCustomizerProps) {
  const {
    currentEvent
  } = useEvent();
  const {
    t
  } = useLanguage();
  const {
    quickLinks,
    availableLinks,
    updateQuickLinks,
    resetToDefaults
  } = useQuickLinks();
  const [localQuickLinks, setLocalQuickLinks] = useState<QuickLink[]>([]);
  const [localAvailableLinks, setLocalAvailableLinks] = useState<QuickLink[]>([]);
  const [draggedIndex, setDraggedIndex] = useState<number | null>(null);
  // Sync local state when modal opens or context changes
  useEffect(() => {
    if (isOpen) {
      setLocalQuickLinks([...quickLinks]);
      setLocalAvailableLinks([...availableLinks]);
    }
  }, [isOpen, quickLinks, availableLinks]);
  // Handle drag start
  const handleDragStart = (index: number) => {
    setDraggedIndex(index);
  };
  // Handle drag over
  const handleDragOver = (e: React.DragEvent, index: number) => {
    e.preventDefault();
    if (draggedIndex === null || draggedIndex === index) return;
    const newLinks = [...localQuickLinks];
    const draggedItem = newLinks[draggedIndex];
    newLinks.splice(draggedIndex, 1);
    newLinks.splice(index, 0, draggedItem);
    setLocalQuickLinks(newLinks);
    setDraggedIndex(index);
  };
  // Handle drag end - clear dragged state
  const handleDragEnd = () => {
    setDraggedIndex(null);
  };
  // Handle drop - ensure dragged state is cleared
  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    setDraggedIndex(null);
  };
  // Touch handling for mobile drag and drop
  const [touchStartY, setTouchStartY] = useState<number | null>(null);
  const [touchCurrentY, setTouchCurrentY] = useState<number | null>(null);
  const handleTouchStart = (e: React.TouchEvent, index: number) => {
    setDraggedIndex(index);
    setTouchStartY(e.touches[0].clientY);
    setTouchCurrentY(e.touches[0].clientY);
  };
  const handleTouchMove = (e: React.TouchEvent) => {
    if (draggedIndex === null || touchStartY === null) return;
    setTouchCurrentY(e.touches[0].clientY);
    const touch = e.touches[0];
    const element = document.elementFromPoint(touch.clientX, touch.clientY);
    const card = element?.closest('[data-quick-link-index]');
    if (card) {
      const hoverIndex = parseInt(card.getAttribute('data-quick-link-index') || '0');
      if (hoverIndex !== draggedIndex) {
        const newLinks = [...localQuickLinks];
        const draggedItem = newLinks[draggedIndex];
        newLinks.splice(draggedIndex, 1);
        newLinks.splice(hoverIndex, 0, draggedItem);
        setLocalQuickLinks(newLinks);
        setDraggedIndex(hoverIndex);
      }
    }
  };
  const handleTouchEnd = () => {
    setDraggedIndex(null);
    setTouchStartY(null);
    setTouchCurrentY(null);
  };
  // Add a link to quick links
  const handleAddLink = (link: QuickLink) => {
    if (localQuickLinks.length >= 4) return;
    // Add to quick links
    const newQuickLinks = [...localQuickLinks, link];
    setLocalQuickLinks(newQuickLinks);
    // Remove from available links
    const newAvailableLinks = localAvailableLinks.filter(l => l.id !== link.id);
    setLocalAvailableLinks(newAvailableLinks);
  };
  // Remove a link from quick links
  const handleRemoveLink = (linkId: string) => {
    // Find the link being removed
    const removedLink = localQuickLinks.find(l => l.id === linkId);
    if (!removedLink) return;
    // Remove from quick links
    const newQuickLinks = localQuickLinks.filter(l => l.id !== linkId);
    setLocalQuickLinks(newQuickLinks);
    // Add back to available links
    const newAvailableLinks = [...localAvailableLinks, removedLink];
    setLocalAvailableLinks(newAvailableLinks);
  };
  // Save changes
  const handleSave = () => {
    updateQuickLinks(localQuickLinks);
    onClose();
  };
  // Reset to defaults
  const handleReset = () => {
    resetToDefaults();
    // Context will update, which will trigger the useEffect above
  };
  // Cancel changes
  const handleCancel = () => {
    setLocalQuickLinks([...quickLinks]);
    setLocalAvailableLinks([...availableLinks]);
    onClose();
  };
  const getTextColor = () => {
    if (currentEvent.id === 'active') {
      return '#17313a';
    }
    return 'white';
  };
  if (!isOpen) return null;
  return <>
      {/* Backdrop */}
      <div className="fixed inset-0 bg-black bg-opacity-50 z-50" onClick={handleCancel} data-id="element-4031" />

      {/* Modal */}
      <div className="fixed inset-x-0 bottom-0 z-50 bg-white rounded-t-2xl shadow-2xl max-h-[85vh] overflow-hidden flex flex-col animate-slide-up" data-id="element-4032">
        {/* Header */}
        <div className="p-4 flex items-center justify-between border-b border-gray-200" style={{
        backgroundColor: currentEvent.colors.primary,
        color: getTextColor()
      }} data-id="element-4033">
          <div data-id="element-4034">
            <h3 className="text-lg font-bold font-gotham" data-id="element-4035">
              Customize Quick Links
            </h3>
            <p className="text-sm opacity-90 font-gotham" data-id="element-4036">
              Drag to reorder, tap to add/remove
            </p>
          </div>
          <button onClick={handleCancel} className="p-2 hover:bg-white hover:bg-opacity-20 rounded-lg transition-colors" data-id="element-4037">
            <XIcon className="w-6 h-6" data-id="element-4038" />
          </button>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto p-4 space-y-6" data-id="element-4039">
          {/* Current Quick Links */}
          <div data-id="element-4040">
            <div className="flex items-center justify-between mb-3" data-id="element-4041">
              <h4 className="text-sm font-bold text-gray-900 font-gotham" data-id="element-4042">
                Your Quick Links ({localQuickLinks.length}/4)
              </h4>
              <button onClick={handleReset} className="flex items-center gap-1 text-xs font-medium font-gotham hover:opacity-70 transition-opacity" style={{
              color: currentEvent.id === 'active' ? currentEvent.colors.secondary : currentEvent.colors.primary
            }} data-id="element-4043">
                <RotateCcwIcon className="w-3 h-3" data-id="element-4044" />
                Reset
              </button>
            </div>

            {localQuickLinks.length === 0 ? <div className="text-center py-8 text-gray-500 font-gotham text-sm" data-id="element-4045">
                No quick links selected. Add some below!
              </div> : <div className="grid grid-cols-2 gap-3" data-id="element-4046">
                {localQuickLinks.map((link, index) => {
              const Icon = link.icon;
              const isDragging = draggedIndex === index;
              return <div key={`${link.id}-${index}`} data-quick-link-index={index} draggable onDragStart={() => handleDragStart(index)} onDragOver={e => handleDragOver(e, index)} onDragEnd={handleDragEnd} onDrop={handleDrop} onTouchStart={e => handleTouchStart(e, index)} onTouchMove={handleTouchMove} onTouchEnd={handleTouchEnd} className="relative rounded-xl border-2 p-4 flex flex-col items-center gap-2 cursor-move transition-opacity touch-none" style={{
                borderColor: currentEvent.colors.accent,
                backgroundColor: 'white',
                opacity: isDragging ? 0.5 : 1
              }} data-id="element-4047">
                      {/* Drag Handle */}
                      <div className="absolute top-2 left-2" data-id="element-4048">
                        <GripVerticalIcon className="w-4 h-4 text-gray-400" data-id="element-4049" />
                      </div>

                      {/* Remove Button */}
                      <button onClick={() => handleRemoveLink(link.id)} className="absolute top-2 right-2 p-1 hover:bg-red-50 rounded-full transition-colors" data-id="element-4050">
                        <XIcon className="w-4 h-4 text-red-500" data-id="element-4051" />
                      </button>

                      {/* Icon */}
                      <div className="w-12 h-12 rounded-full flex items-center justify-center mt-2" style={{
                  backgroundColor: currentEvent.colors.primary,
                  color: currentEvent.id === 'active' ? '#17313a' : 'white'
                }} data-id="element-4052">
                        <Icon className="w-6 h-6" data-id="element-4053" />
                      </div>

                      {/* Label */}
                      <span className="text-[10px] font-semibold font-gotham text-center leading-tight" style={{
                  color: currentEvent.id === 'active' ? currentEvent.colors.secondary : currentEvent.colors.primary
                }} data-id="element-4054">
                        {t(link.labelKey)}
                      </span>
                    </div>;
            })}
              </div>}
          </div>

          {/* Available Links */}
          {localAvailableLinks.length > 0 && <div data-id="element-4055">
              <h4 className="text-sm font-bold text-gray-900 font-gotham mb-3" data-id="element-4056">
                Available Links
              </h4>
              <div className="grid grid-cols-2 gap-3" data-id="element-4057">
                {localAvailableLinks.map(link => {
              const Icon = link.icon;
              const canAdd = localQuickLinks.length < 4;
              return <button key={link.id} onClick={() => canAdd && handleAddLink(link)} disabled={!canAdd} className={`relative rounded-xl border-2 border-gray-200 p-4 flex flex-col items-center gap-2 transition-all ${canAdd ? 'hover:border-gray-400 hover:shadow-md active:scale-95' : 'opacity-50 cursor-not-allowed'}`} data-id="element-4058">
                      {/* Add Icon */}
                      {canAdd && <div className="absolute top-2 right-2" data-id="element-4059">
                          <div className="w-5 h-5 rounded-full flex items-center justify-center" style={{
                    backgroundColor: currentEvent.colors.primary,
                    color: currentEvent.id === 'active' ? '#17313a' : 'white'
                  }} data-id="element-4060">
                            <PlusIcon className="w-3 h-3" data-id="element-4061" />
                          </div>
                        </div>}

                      {/* Icon */}
                      <div className="w-12 h-12 rounded-full bg-gray-100 flex items-center justify-center" data-id="element-4062">
                        <Icon className="w-6 h-6 text-gray-600" data-id="element-4063" />
                      </div>

                      {/* Label */}
                      <span className="text-[10px] font-semibold font-gotham text-center leading-tight text-gray-700" data-id="element-4064">
                        {t(link.labelKey)}
                      </span>
                    </button>;
            })}
              </div>
            </div>}
        </div>

        {/* Footer */}
        <div className="p-4 border-t border-gray-200 flex gap-3" data-id="element-4065">
          <Button variant="outline" fullWidth onClick={handleCancel} data-id="element-4066">
            Cancel
          </Button>
          <Button variant="primary" fullWidth onClick={handleSave} disabled={localQuickLinks.length === 0} data-id="element-4067">
            Save Changes
          </Button>
        </div>
      </div>

      <style data-id="element-4068">{`
        @keyframes slide-up {
          from {
            transform: translateY(100%);
          }
          to {
            transform: translateY(0);
          }
        }
        .animate-slide-up {
          animation: slide-up 0.3s ease-out;
        }
      `}</style>
    </>;
}